import { z } from 'zod'
import { objectToCamel } from 'ts-case-convert'
import { skuSchema } from './sku'
import { optionValueSchema } from './option'

//* ---------------- BASE ---------------- *//
const baseMatchMaterialSchema = z.object({
  id: z.number(),
  description: z.string().nullable(),
  image: z.string().url().nullable(),
})

const baseMaterialSchema = z.object({
  id: z.number(),
  value: z.string(),
  description: z.string(),
  code: z.string().nullable(),
  title: z.string(),
  image: z.string().nullable().optional(),
  rgb: z.string().nullable(),
  display_order: z.number(),
  active: z.boolean(),
})

const baseButtonSchema = baseMaterialSchema.extend({
  garment_type: z.coerce.number(),
  price_tier: z.coerce.number(),
})

/** linings are a special form of fabric+material */
export const liningMaterialSchema = z.object({
  id: z.number(),
  description: z.string(),
  image: z.string().url().nullable().optional(),
  trinity_number: z.string(),
})

/** Dealer Labels are a very special form of material, so share almost nothing with the rest */
const baseDealerLabelSchema = z.object({
  id: z.coerce.number(),
  dealer_id: z.coerce.number(),
  sku_id: z.coerce.number().nullable(),
  garment_type: z.coerce.number(),
  updated_at: z.coerce.date(),
  created_at: z.coerce.date(),
  is_active: z.coerce.boolean(),
  stock_available: z.coerce.number(),
  rate_of_consumption: z.coerce.number(),
  dealer: z.object({
    id: z.number(),
    name: z.string(),
  }),
})

//* ---------------- SCHEMAS ---------------- *//
export const materialSchema = baseMaterialSchema.transform(objectToCamel)

export const buttonSchema = baseButtonSchema.transform(objectToCamel)

export const buttonExtendedSchema = baseButtonSchema
  .extend({
    skus: z.array(skuSchema).nullable(),
    option_values: z.array(optionValueSchema).nullable(),
  })
  .transform(objectToCamel)

export const dealerLabelSchema = baseDealerLabelSchema.transform(objectToCamel)

export const feltSchema = baseMaterialSchema.transform(objectToCamel)

export const suedeSchema = baseMaterialSchema.transform(objectToCamel)

export const pipingSchema = z
  .object({
    id: z.number(),
    trinity_fabric_number: z.string(),
    lining_fabric_number: z.string(),
    description: z.string(),
    image: z.string().url(),
    active: z.boolean(),
  })
  .transform(objectToCamel)

export const nameLabelSchema = baseMaterialSchema.omit({ code: true, title: true }).transform(objectToCamel)

export const ribknitSchema = baseMaterialSchema.omit({ code: true, title: true }).transform(objectToCamel)

export const threadSchema = baseMaterialSchema.omit({ title: true }).transform(objectToCamel)

export const trouserTrimSchema = baseMaterialSchema
  .omit({
    title: true,
    code: true,
    rgb: true,
  })
  .extend({
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
  })
  .transform(objectToCamel)

export const waistbandSchema = z
  .object({
    id: z.number(),
    description: z.string(),
    image: z.string().nullable(),
    value: z.string().nullable(),
  })
  .transform(objectToCamel)

export const zippersSchema = baseMaterialSchema
  .omit({
    title: true,
    code: true,
    rgb: true,
  })
  .extend({
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
  })
  .transform(objectToCamel)

export const matchMaterialSchema = baseMatchMaterialSchema.transform(objectToCamel)

//* ---------------- TYPES ---------------- *//
declare global {
  namespace TrinityAPI {
    type LiningType = z.infer<typeof liningMaterialSchema>
    type ButtonType = z.infer<typeof buttonSchema>
    type ButtonExtendedType = z.infer<typeof buttonExtendedSchema>
    type DealerLabelType = z.infer<typeof dealerLabelSchema>
    type FeltType = z.infer<typeof feltSchema>
    type SuedeType = z.infer<typeof suedeSchema>
    type PipingType = z.infer<typeof pipingSchema>
    type NameLabelType = z.infer<typeof nameLabelSchema>
    type RibknitType = z.infer<typeof ribknitSchema>
    type ThreadType = z.infer<typeof threadSchema>
    type TrouserTrimType = z.infer<typeof trouserTrimSchema>
    type WaistbandType = z.infer<typeof waistbandSchema>
    type ZipperType = z.infer<typeof zippersSchema>
    type MatchMaterialType = z.infer<typeof matchMaterialSchema>
  }
}
